import { useState, useCallback } from 'react'
import { satsueiDataKensakuService } from '../services/satsueiDataKensakuService'
import { useApiError } from '../../shared/hooks/useApiError'

export const useSatsueiDataKensaku = (errorHandlers = null) => {
  // Error handling - use provided handlers or create new ones
  const defaultErrorHandlers = useApiError()
  const { handleApiError, handleApiResponse } = errorHandlers || defaultErrorHandlers

  // Search state
  const [searchParams, setSearchParams] = useState({
    objectId: '',
    objectIdFrom: '',
    objectIdTo: '',
    satsueiMemoCategoryId: '',
    kyotenId: '',
    shokiTorokusha: '',
    shokiTorokuJikokuJokenFrom: '1',
    shokiTorokuJikokuKikanFrom: '',
    shokiTorokuJikokuDateFrom: '',
    shokiTorokuJikokuJokenTo: '1',
    shokiTorokuJikokuKikanTo: '',
    shokiTorokuJikokuDateTo: '',
    saishuKoshinsha: '',
    saishuKoshinJikokuJokenFrom: '1',
    saishuKoshinJikokuKikanFrom: '',
    saishuKoshinJikokuDateFrom: '',
    saishuKoshinJikokuJokenTo: '1',
    saishuKoshinJikokuKikanTo: '',
    saishuKoshinJikokuDateTo: '',
    sort: '1',
    noSatsueiMemoRegist: false,
    noImageRegist: false,
    imageRegist: false
  })

  // UI state
  const [isLoading, setIsLoading] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  
  // Data state
  const [satsueiMemoCategories, setSatsueiMemoCategories] = useState([])
  const [kyotenList, setKyotenList] = useState([])
  const [pixUserList, setPixUserList] = useState([])

  // Load initial data
  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true)
      
      // Load each API call individually to handle errors properly
      try {
        const categoriesRes = await satsueiDataKensakuService.getSatsueiMemoCategory({ isContainSatsueiMemoColumn: false })
        if (!handleApiResponse(categoriesRes, 'カテゴリーデータの取得に失敗しました。')) {
          setSatsueiMemoCategories(categoriesRes.satsueiMemoCategoryList || [])
        }
      } catch (error) {
        handleApiError(error, 'カテゴリーデータの取得に失敗しました。')
      }

      try {
        const kyotenRes = await satsueiDataKensakuService.getKyotenList()
        if (!handleApiResponse(kyotenRes, '拠点データの取得に失敗しました。')) {
          setKyotenList(kyotenRes.kyotenList || [])
        }
      } catch (error) {
        handleApiError(error, '拠点データの取得に失敗しました。')
      }

      try {
        const usersRes = await satsueiDataKensakuService.getPixUserList()
        if (!handleApiResponse(usersRes, 'ユーザーデータの取得に失敗しました。')) {
          setPixUserList(usersRes.pixUserList || [])
        }
      } catch (error) {
        handleApiError(error, 'ユーザーデータの取得に失敗しました。')
      }
    } catch (error) {
      handleApiError(error, '初期データの読み込みに失敗しました。')
    } finally {
      setIsLoading(false)
    }
  }, [handleApiError, handleApiResponse])

  // Search function
  const performSearch = useCallback(async (customSearchParams = null) => {
    try {
      setIsLoading(true)
      
      const searchData = customSearchParams || searchParams
      
      const response = await satsueiDataKensakuService.getObjectList(searchData)
      
      // Check for business logic errors
      const hasError = handleApiResponse(response, '検索に失敗しました。')
      
      if (!hasError) {
        setSearchResults(response.objectDatas || [])
      }
    } catch (error) {
      handleApiError(error, '検索に失敗しました。')
    } finally {
      setIsLoading(false)
    }
  }, [searchParams, handleApiError, handleApiResponse])

  // Download function (downloadType: 0 = all, 1 = image only, 2 = csv only; all responses are ZIP)
  const downloadResults = useCallback(async (downloadType = 0) => {
    try {
      setIsLoading(true)

      const response = await satsueiDataKensakuService.getObjectListDownload({
        ...searchParams,
        downloadType: downloadType ?? 0
      })

      // Create download link
      const blob = new Blob([response], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `撮影データ${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      handleApiError(error, 'ダウンロードに失敗しました。')
    } finally {
      setIsLoading(false)
    }
  }, [searchParams, handleApiError])

  // Clear all search conditions
  const clearSearchConditions = useCallback(() => {
    setSearchParams({
      objectId: '',
      objectIdFrom: '',
      objectIdTo: '',
      satsueiMemoCategoryId: '',
      kyotenId: '',
      shokiTorokusha: '',
      shokiTorokuJikokuJokenFrom: '1',
      shokiTorokuJikokuKikanFrom: '',
      shokiTorokuJikokuDateFrom: '',
      shokiTorokuJikokuJokenTo: '1',
      shokiTorokuJikokuKikanTo: '',
      shokiTorokuJikokuDateTo: '',
      saishuKoshinsha: '',
      saishuKoshinJikokuJokenFrom: '1',
      saishuKoshinJikokuKikanFrom: '',
      saishuKoshinJikokuDateFrom: '',
      saishuKoshinJikokuJokenTo: '1',
      saishuKoshinJikokuKikanTo: '',
      saishuKoshinJikokuDateTo: '',
      sort: '1',
      noSatsueiMemoRegist: false,
      noImageRegist: false,
      imageRegist: false
    })
    //setSearchResults([])
  }, [])

  // Update search params
  const updateSearchParams = useCallback((updates) => {
    setSearchParams(prev => ({ ...prev, ...updates }))
  }, [])

  return {
    // State
    searchParams,
    isLoading,
    searchResults,
    satsueiMemoCategories,
    kyotenList,
    pixUserList,
    
    // Actions
    loadInitialData,
    performSearch,
    downloadResults,
    clearSearchConditions,
    updateSearchParams
  }
}
